import apiClient from './client';
import { User, UserRole, Pagination, ApiResponse } from '@/types';

/**
 * User query parameters
 */
export interface UserQueryParams {
  page?: number;
  limit?: number;
  role?: UserRole;
  active?: boolean;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/**
 * User list response
 */
export interface UserListResponse {
  data: User[];
  pagination: Pagination;
}

/**
 * Get users with filters
 */
export async function getUsers(params?: UserQueryParams): Promise<UserListResponse> {
  const response = await apiClient.get<ApiResponse<User[]> & { meta?: { pagination: Pagination } }>('/users', { params });
  return {
    data: response.data.data as User[],
    pagination: response.data.meta?.pagination || {
      page: 1,
      limit: 20,
      total: 0,
      totalPages: 0,
      hasNext: false,
      hasPrev: false,
    },
  };
}

/**
 * Get a single user by ID
 */
export async function getUser(id: string): Promise<User> {
  const response = await apiClient.get<ApiResponse<User>>(`/users/${id}`);
  return response.data.data as User;
}

/**
 * Get current user profile
 */
export async function getCurrentUser(): Promise<User> {
  const response = await apiClient.get<ApiResponse<User>>('/auth/me');
  return response.data.data as User;
}

/**
 * Update user data
 */
export interface UpdateUserData {
  displayName?: string;
  role?: UserRole;
  gitlabUsername?: string;
  rocketchatUsername?: string;
  timezone?: string;
  active?: boolean;
}

export async function updateUser(id: string, data: UpdateUserData): Promise<User> {
  const response = await apiClient.patch<ApiResponse<User>>(`/users/${id}`, data);
  return response.data.data as User;
}

/**
 * Sync users from LDAP
 */
export interface LdapSyncResult {
  created: number;
  updated: number;
  deactivated: number;
  total: number;
}

export async function syncFromLdap(): Promise<LdapSyncResult> {
  const response = await apiClient.post<ApiResponse<LdapSyncResult>>('/users/sync');
  return response.data.data as LdapSyncResult;
}

/**
 * Get user metrics
 */
export interface UserMetrics {
  totalCommits: number;
  avgQualityScore: number;
  standupResponseRate: number;
}

export async function getUserMetrics(id: string): Promise<UserMetrics> {
  const response = await apiClient.get<ApiResponse<UserMetrics>>(`/users/${id}/metrics`);
  return response.data.data as UserMetrics;
}
